/** @jsx jsx */

import { jsx } from '@emotion/core';
import { TimePeriodDays } from 'app/developer-v3/hooks/useIntegrationInsights';
import { getAppUrl } from 'app/developer-v3/utils';
import { Icon } from '@zapier/zinnia-icons';
import { Link, Text } from '@zapier/design-system';
import { BaseTile, BaseTileState } from './BaseTile';
import DateRangeDropdown from '../DateRangeDropdown';
import { defaultInitialSelectedItem, timePeriodDaysItems } from '../utils';
import CountDisplay from '../CountDisplay';
import withTrackInteraction, {
  WithPartnerUserInteractionTrackerProps,
} from 'app/developer-v3/teams/team-partner-sharing/withPartnerUserInteractionTracker';

interface NewUserTileProps
  extends WithPartnerUserInteractionTrackerProps,
    BaseTileState {
  appId: string;
  newUsersCount: number;
  onSelect: (value: TimePeriodDays) => void;
  dataTestId?: string;
}

export const NewUsersTile = ({
  appId,
  handleTrackInteraction,
  isLoading,
  isError,
  newUsersCount,
  dataTestId,
  onSelect,
}: NewUserTileProps) => (
  <BaseTile
    dataTestId={dataTestId}
    dropdowns={[
      <DateRangeDropdown
        initialSelectedItem={defaultInitialSelectedItem}
        items={timePeriodDaysItems}
        key="NewUsersTile-DateRangeDropdown"
        onSelect={period => onSelect(period as TimePeriodDays)}
      />,
    ]}
    headerContent="New users"
    icon={<Icon name="personOne" size="24px" />}
    isError={isError}
    isLoading={isLoading}
    isNoData={newUsersCount === undefined || newUsersCount === null}
    tipContent={
      <Text type="Body1">
        To get more users,{' '}
        <Link
          href={`${getAppUrl(appId)}/embed`}
          onPress={() =>
            handleTrackInteraction({
              eventAction: 'click',
              interactionGoal:
                'Views embed zapier details from Insights dashboard',
              interactionName: 'embed_zapier_in_your_product',
            })
          }
        >
          embed Zapier in your product.
        </Link>
      </Text>
    }
    tipContentStyle={{ minHeight: 60 }}
    toolTipContent={
      <Text color="PrimeWhite" type="Body1">
        Total number of new users using the integration over a selected period
        of time. A new user = first time a user creates and activates a Zap with
        this integration.
      </Text>
    }
  >
    <CountDisplay count={newUsersCount} />
  </BaseTile>
);

export default withTrackInteraction(NewUsersTile);
